import { request } from "@/lib/apiClient";
import {
  getLatestBrowserLocation,
  requireRequiredBrowserLocation,
} from "@/lib/location";

const AUTH_LOOKUP_RETRY_DELAY_MS = 400;

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function retrySafeAuthLookup(submit) {
  try {
    return await submit();
  } catch (error) {
    if (error?.status !== 0) {
      throw error;
    }
    await wait(AUTH_LOOKUP_RETRY_DELAY_MS);
    return submit();
  }
}

function withTimezone(input) {
  return {
    ...input,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone?.trim() || "",
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
  };
}

async function withOptionalLoginLocation(input, submit) {
  return submit(input.location ?? getLatestBrowserLocation());
}

export async function startAuthOTP(input) {
  return request("/api/v1/auth/otp/start", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export async function checkAuthEmail(input) {
  return retrySafeAuthLookup(() => request("/api/v1/auth/email/check", {
    body: JSON.stringify(input),
    method: "POST",
  }));
}

export async function verifyAuthOTP(input) {
  return request("/api/v1/auth/otp/verify", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export async function registerShop(input) {
  let location = input.location ?? getLatestBrowserLocation();
  if (!location) {
    try { location = await requireRequiredBrowserLocation({ fresh: false }); } catch { /* Country selection remains available if permission is denied. */ }
  }
  return request("/api/v1/auth/register", {
    body: JSON.stringify(withTimezone({ ...input, location })),
    method: "POST",
  });
}

export async function loginWithPassword(input) {
  return withOptionalLoginLocation(input, (location) => request("/api/v1/auth/login", {
    body: JSON.stringify(withTimezone({ ...input, location })),
    method: "POST",
  }));
}

export async function resetLoginPassword(input) {
  return withOptionalLoginLocation(input, (location) => request("/api/v1/auth/recover/reset-password", {
    body: JSON.stringify({ ...input, location }),
    method: "POST",
  }));
}

export async function setupStaffPassword(input) {
  return withOptionalLoginLocation(input, (location) => request("/api/v1/auth/staff/setup-password", {
    body: JSON.stringify({ ...input, location }),
    method: "POST",
  }));
}

export async function logoutSession(sessionId) {
  return request("/api/v1/auth/logout", {
    body: JSON.stringify({}),
    method: "POST",
    sessionId,
  });
}
